'use client'

import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import Image from 'next/image';
import { motion, AnimatePresence, Variants } from 'framer-motion';

import { ParagraphSection } from '@/types/drupal';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { trackEvent } from '@/lib/analytics';

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] } 
  }
};

const tabContentVariants: Variants = {
  initial: { opacity: 0, x: 10 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, x: -10, transition: { duration: 0.3 } }
};

const t = {
  en: { cta: 'See more about this' },
  es: { cta: 'Ver más sobre este proyecto' },
} as const;

export default function Projects({ data, lang = 'en' }: { data: ParagraphSection; lang?: string }) {
  const [activeTab, setActiveTab] = useState(0);
  const projects = data.projects || [];
  const activeProject = projects[activeTab];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const checkIcon = `<svg class="li-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="#05A2F1" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`;

  const processDescription = (html: string) => {
    const baseUrl = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL || '';
    return html
      .replace(/src="\/sites\/default\/files\//g, `src="${baseUrl}/sites/default/files/`)
      .replace(/<li>/g, `<li>${checkIcon}`);
  };

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const onReInit = useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList());
    onSelect(api);
  }, [onSelect]);

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => onReInit(emblaApi));
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onReInit);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onReInit);
    };
  }, [emblaApi, activeTab, onSelect, onReInit]);

  if (projects.length === 0) return null;

  return (
    <section id="projects" className="Projects py-[30px] overflow-hidden" aria-label="Projects">
      <div className="site-container lg:border-t lg:border-b lg:border-[#D9D9D9]">
        <div className="bg-[#E1E8EF]">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="section-container py-[60px] lg:py-[110px]"
          >
            {/* Header Block */}
            <div className="text-center lg:text-left">
              <motion.div variants={fadeInUp} className="section-heading text-[#0A4492]" dangerouslySetInnerHTML={{ __html: data.title?.processed || '' }} />
              <motion.div variants={fadeInUp} className="text-[#0A4492]/70 text-lg max-w-[870px] mt-5" dangerouslySetInnerHTML={{ __html: data.body?.processed || '' }} />
            </div>

            {/* Tab Navigation */}
            <motion.div
              variants={fadeInUp}
              role="tablist"
              aria-label="Project selection"
              className="flex gap-2 lg:gap-4 mt-[30px] lg:mt-[60px] border-t border-[#D3D4D7] overflow-x-auto no-scrollbar"
            >
              {projects.map((project, index) => (
                <button
                  key={project.id}
                  role="tab"
                  aria-selected={activeTab === index}
                  aria-controls={`project-panel-${index}`}
                  id={`project-tab-${index}`}
                  onClick={() => {
                    setActiveTab(index);
                    trackEvent('project_view', {
                      project_name: project.title,
                      project_type: project.projectType?.name,
                    });
                  }}
                  className={`
                    p-1 text-left transition-all min-w-[150px] border-t-2 cursor-pointer
                    ${activeTab === index
                      ? 'border-[#0A4492] opacity-100'
                      : 'border-transparent opacity-40 hover:opacity-70'
                    }
                  `}
                >
                  <p
                    className={`font-bold text-sm leading-5 lg:text-[19px] lg:leading-7
                      ${activeTab === index
                        ? 'text-[#404040]'
                        : 'text-[#6C7585]'
                      }
                    `}
                  >{project.title}</p>

                  <p
                    className={`text-xs leading-6 lg:text-[16px] lg:leading-7
                      ${activeTab === index
                        ? 'text-[#05A2F1]'
                        : 'text-[#6C7585]'
                      }
                    `}
                  >{project.projectType?.name || 'Project'}</p>
                </button>
              ))}
            </motion.div>

            {/* Project Content Area */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                role="tabpanel"
                id={`project-panel-${activeTab}`}
                aria-labelledby={`project-tab-${activeTab}`}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={tabContentVariants}
                className="grid grid-cols-1 lg:grid-cols-12 lg:gap-[100px] mt-[30px] lg:mt-[60px]"
              >
                <div className="lg:col-span-6 flex flex-col items-center">
                  <div
                    className="relative w-full overflow-hidden"
                    ref={emblaRef}
                    role="region"
                    aria-roledescription="carousel"
                    aria-label={`${activeProject.title} images`}
                  >
                    <div className="flex">
                      {activeProject.images?.map((img, idx) => (
                        <div
                          key={idx}
                          role="group"
                          aria-roledescription="slide"
                          aria-label={`Image ${idx + 1} of ${activeProject.images?.length || 0}`}
                          className="flex-[0_0_100%] min-w-0 px-2"
                        >
                          <div className="relative aspect-[16/10] shadow-lg rounded-lg bg-[#D3DCE6] overflow-hidden">
                            <Image
                              src={img.mediaImage.url}
                              alt={img.mediaImage.alt || `${activeProject.title} screenshot`}
                              fill
                              className="object-contain"
                              sizes="(max-width: 1024px) 95vw, 45vw"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Live region for image carousel changes */}
                  <div aria-live="polite" aria-atomic="true" className="sr-only">
                    Image {selectedIndex + 1} of {scrollSnaps.length}
                  </div>

                  <div className="mt-8 flex items-center justify-center gap-4 w-full max-w-[440px] mx-auto">
                    {/* PREV BUTTON */}
                    <button
                      aria-label="Previous image"
                      disabled={!canScrollPrev}
                      className={`transition-colors duration-200 ${canScrollPrev ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
                      onClick={() => emblaApi?.scrollPrev()}
                    >
                      <Icon
                        name="ChevronLeft"
                        size={32}
                        color="#0A4492"
                      />
                    </button>

                    {/* PROGRESS PILL CONTAINER */}
                    <div
                      className="relative flex-1 h-12 bg-[#FDFDFD] rounded-full border border-[#C2C2C2] flex items-center px-[22px]"
                      role="group"
                      aria-label={`Image ${selectedIndex + 1} of ${scrollSnaps.length}`}
                    >
                      <div className="flex w-full gap-2 items-center">
                        {scrollSnaps.map((_, index) => (
                          <div
                            key={index}
                            className="relative flex-1 h-0.5 bg-[#E0E0E0] rounded-full overflow-hidden"
                            aria-hidden="true"
                          >
                            <motion.div
                              initial={false}
                              animate={{ x: index === selectedIndex ? '0%' : '-100%' }}
                              transition={{ duration: 0.5, ease: "easeInOut" }}
                              className={`
                                absolute inset-0 bg-[#3CABE2] transition-transform duration-500 ease-out 
                                ${index === selectedIndex ? 'translate-x-0' : '-translate-x-full'
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* NEXT BUTTON */}
                    <button
                      aria-label="Next image"
                      disabled={!canScrollNext}
                      className={`transition-colors duration-200 ${canScrollNext ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
                      onClick={() => emblaApi?.scrollNext()}
                    >
                      <Icon
                        name="ChevronRight"
                        size={32}
                        color="#0A4492"
                      />
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-6 text-[#0A4492] mt-10 lg:mt-0 flex flex-col items-center lg:items-start">
                  <div
                    className="
                      prose prose-blue max-w-none mb-12 text-center sm:text-left
                      [&_ul]:list-none [&_ul]:pl-0
                      [&_li]:flex [&_li]:items-start [&_li]:mb-4
                      [&_li]:text-[14px] [&_li]:leading-[1.4] [&_li]:lg:text-[18px] [&_li]:lg:leading-[26px]
                      [&_.li-icon]:shrink-0 [&_.li-icon]:mt-0.5 [&_.li-icon]:mr-3
                      [&_p]:text-[#002E84] [&_p]:text-[14px] [&_p]:leading-[1.2] [&_p]:lg:text-[22px] [&_p]:lg:leading-7 [&_p]:mb-7.5 [&_p]:lg:mb-10
                    "
                    dangerouslySetInnerHTML={{ __html: processDescription(activeProject.description?.processed || '') }}
                  />

                  <div className="w-full flex justify-center lg:justify-start">
                    <Button
                      label={t[lang as keyof typeof t]?.cta ?? t.en.cta}
                      href={activeProject.path || '#'}
                      onClick={() => trackEvent('cta_click', {
                        cta_label: t[lang as keyof typeof t]?.cta ?? t.en.cta,
                        cta_location: 'projects',
                        cta_url: activeProject.path || '#',
                      })}
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
